import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router"
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from "motion/react"
import {
  ArrowLeft,
  ArrowRight,
  CalendarCheck01,
  ChevronRight,
  Coins03,
  Target04,
  Trophy01,
  Wallet01,
  X,
  ZapFast,
} from "@untitledui/icons"

import type {
  DealershipGoalsRewards,
  DealershipRewardBundle,
  ServiceLaneBaseline,
} from "@/lib/api/types"
import { withGoalsRewardsDefaults } from "@/lib/dashboard/defaults"
import { cx } from "@/lib/utils/cx"

// First-time Service Advisor tour. Each slide previews a real dashboard
// surface in a soft "preview window" — content motion (counters tick,
// statuses morph, dropdowns unfurl) demonstrates the product before the
// advisor ever lands on it. One-line headline per slide; no paragraph
// cards, no utility-icon hero art.
//
// Skipped at first launch via the existing `onSkip` flow.

interface SATourProps {
  firstName: string
  goalsRewards: DealershipGoalsRewards | undefined
  baseline: ServiceLaneBaseline | undefined
  activeAdvisorCount: number
  onComplete: () => void
  onSkip: () => void
}

interface SlideRenderer {
  key: string
  render: () => React.ReactNode
}

export function SATour({
  firstName,
  goalsRewards,
  baseline,
  activeAdvisorCount,
  onComplete,
  onSkip,
}: SATourProps) {
  const navigate = useNavigate()

  const effective = useMemo(
    () => withGoalsRewardsDefaults(goalsRewards, baseline, activeAdvisorCount),
    [goalsRewards, baseline, activeAdvisorCount],
  )
  const rewards = effective.rewards ?? {}
  const perEstimatePay = moneyOf(rewards.perEstimateSubmitted)
  const perPaidPay = moneyOf(rewards.perEstimateCompleted)
  const payoutCadence = effective.payoutCadence ?? "biweekly"
  const contestCount = useMemo(() => countEnabledContests(effective), [effective])
  const hasContests = contestCount > 0

  const slides: SlideRenderer[] = useMemo(() => {
    const list: SlideRenderer[] = []
    list.push({
      key: "wallet",
      render: () => (
        <Slide
          eyebrow={`Welcome, ${firstName}`}
          headline="Your work and your pay, all live."
          subline="Write an estimate, send it to the body shop, watch your wallet move as they work it through. One screen, both at once."
        >
          <SceneWallet />
        </Slide>
      ),
    })
    list.push({
      key: "earnings",
      render: () => (
        <Slide
          eyebrow="Your earnings"
          headline="Today, this week, this month."
          subline="One card, three time windows. Tap to switch."
        >
          <SceneEarnings />
        </Slide>
      ),
    })
    list.push({
      key: "morph",
      render: () => (
        <Slide
          eyebrow="Pending → Paid"
          headline="Pending becomes paid."
          subline={
            perPaidPay > 0
              ? `Each estimate the body shop completes adds $${perPaidPay}.`
              : "Each estimate the body shop completes adds money to your wallet."
          }
        >
          <SceneEstimateMorph perEstimate={perEstimatePay} perPaid={perPaidPay} />
        </Slide>
      ),
    })
    if (hasContests) {
      list.push({
        key: "contests",
        render: () => (
          <Slide
            eyebrow="Cherry on top"
            headline={
              contestCount === 1
                ? "1 competition is running."
                : `${contestCount} competitions are running.`
            }
            subline="They run automatically — every scan counts. Climb the board."
          >
            <SceneContests count={contestCount} />
          </Slide>
        ),
      })
    }
    list.push({
      key: "go",
      render: () => (
        <Slide
          eyebrow="Your move"
          headline={`Let's go, ${firstName}.`}
          subline={
            perEstimatePay > 0
              ? `Write one, send it to the body shop. $${perEstimatePay} lands now${perPaidPay > 0 ? `, $${perPaidPay} more when they close it` : ""}.`
              : "Write one and send it to the body shop. 40 seconds, lane-side."
          }
        >
          <SceneHero perEstimate={perEstimatePay} perPaid={perPaidPay} />
        </Slide>
      ),
    })
    return list
  }, [
    firstName,
    hasContests,
    contestCount,
    perEstimatePay,
    perPaidPay,
  ])
  void payoutCadence

  const [index, setIndex] = useState(0)
  const [direction, setDirection] = useState<1 | -1>(1)
  const current = slides[index]
  const isLast = index === slides.length - 1
  const isFirst = index === 0

  const goNext = () => {
    if (isLast) {
      onComplete()
      navigate("/estimates/quick?return=/dashboard")
      return
    }
    setDirection(1)
    setIndex((i) => Math.min(slides.length - 1, i + 1))
  }
  const goBack = () => {
    if (isFirst) return
    setDirection(-1)
    setIndex((i) => Math.max(0, i - 1))
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-gradient-to-b from-zinc-50 via-white to-zinc-50">
      {/* Ambient backdrop */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 right-[-200px] size-[680px] rounded-full bg-emerald-200/20 blur-3xl" />
        <div className="absolute top-1/2 -left-40 size-[440px] rounded-full bg-amber-100/20 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.30]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgb(228 228 231 / 0.55) 1px, transparent 0)",
            backgroundSize: "24px 24px",
          }}
        />
      </div>

      {/* Top bar */}
      <div className="relative pt-[max(env(safe-area-inset-top),16px)] px-4 md:px-8">
        <div className="max-w-[640px] mx-auto flex items-center justify-between gap-4 h-12">
          <button
            type="button"
            onClick={onSkip}
            className="inline-flex items-center gap-1 h-9 px-2 -ml-2 rounded-lg text-[12.5px] font-medium text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100/70 transition-colors duration-150"
          >
            <X className="size-3.5" aria-hidden />
            Skip
          </button>
          <div className="flex items-center gap-1.5">
            {slides.map((_, i) => (
              <span
                key={i}
                className={cx(
                  "h-1.5 rounded-full transition-all duration-300",
                  i === index
                    ? "w-6 bg-emerald-500"
                    : i < index
                      ? "w-1.5 bg-emerald-400"
                      : "w-1.5 bg-zinc-200",
                )}
              />
            ))}
          </div>
          <div className="w-9" />
        </div>
      </div>

      {/* Body */}
      <div className="relative h-[calc(100dvh-12rem)] md:h-[calc(100dvh-13rem)] overflow-y-auto px-4 md:px-8">
        <div className="max-w-[560px] mx-auto py-2 md:py-4">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={current.key}
              custom={direction}
              variants={SLIDE_VARIANTS}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.36, ease: [0.16, 1, 0.3, 1] }}
            >
              {current.render()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="relative absolute inset-x-0 bottom-0 pb-[max(env(safe-area-inset-bottom),16px)] pt-3 px-4 md:px-8 bg-gradient-to-t from-white via-white/90 to-transparent">
        <div className="max-w-[560px] mx-auto flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={goBack}
            disabled={isFirst}
            className={cx(
              "inline-flex items-center gap-1.5 h-11 px-3 rounded-xl text-[13px] font-semibold transition-all duration-150",
              isFirst
                ? "text-zinc-300 pointer-events-none"
                : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100/70",
            )}
          >
            <ArrowLeft className="size-4" aria-hidden />
            Back
          </button>
          <button
            type="button"
            onClick={goNext}
            className="group inline-flex items-center justify-between gap-3 h-12 pl-5 pr-3 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 hover:from-emerald-600 hover:to-emerald-800 active:scale-[0.99] text-white shadow-xl shadow-emerald-600/25 transition-all duration-200 min-w-[180px]"
          >
            <span className="text-[14px] font-bold tracking-tight">
              {isLast ? "Start an estimate" : "Continue"}
            </span>
            <span className="size-7 rounded-lg bg-white/15 grid place-items-center backdrop-blur-sm">
              {isLast ? (
                <ZapFast className="size-4" aria-hidden />
              ) : (
                <ArrowRight className="size-4 group-hover:translate-x-0.5 transition-transform duration-200" aria-hidden />
              )}
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}

const SLIDE_VARIANTS = {
  enter: (dir: 1 | -1) => ({ opacity: 0, x: dir * 30 }),
  center: { opacity: 1, x: 0 },
  exit: (dir: 1 | -1) => ({ opacity: 0, x: dir * -30 }),
}

/* ════════════════════════ SLIDE FRAME ════════════════════════ */

// Common frame for every slide: eyebrow + headline + subline + preview
// window. The preview window is what carries the design weight — copy is
// tight and lives above it.
function Slide({
  eyebrow,
  headline,
  subline,
  children,
}: {
  eyebrow: string
  headline: string
  subline: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-4 md:gap-5">
      <div className="text-center space-y-1.5 max-w-md">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-emerald-700">
          {eyebrow}
        </p>
        <h1 className="text-[22px] md:text-[26px] font-black text-zinc-900 tracking-tight leading-tight">
          {headline}
        </h1>
        <p className="text-[13px] md:text-[13.5px] text-zinc-500 leading-snug">
          {subline}
        </p>
      </div>
      <PreviewWindow>{children}</PreviewWindow>
    </div>
  )
}

// Soft preview window. No phone chrome — just a rounded shadowed card the
// dashboard pieces live inside. The aspect feels mobile-ish so the advisor
// recognizes "this is a slice of my real screen" without it being App
// Store photography.
function PreviewWindow({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative w-full max-w-[360px] mx-auto">
      <div
        aria-hidden
        className="absolute -inset-3 rounded-[36px] bg-gradient-to-br from-emerald-200/30 via-zinc-100/0 to-amber-200/20 blur-2xl"
      />
      <div className="relative rounded-[28px] border border-zinc-200/70 bg-white shadow-2xl shadow-zinc-900/[0.10] overflow-hidden">
        {/* Faint top wash so the preview reads as a "screen" not a flat card */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-emerald-50/60 to-transparent pointer-events-none"
        />
        <div className="relative px-4 pt-4 pb-4 min-h-[420px]">{children}</div>
      </div>
    </div>
  )
}

/* ════════════════════════ SCENE: WALLET ════════════════════════ */

// Mini dashboard top: greeting + earned pill + wallet glance card with a
// counter that ticks $0 → $230 over ~1.4s. Pulsing ring suggests "tap".
function SceneWallet() {
  return (
    <div className="space-y-3">
      <MockTopStrip showStreak={false} />
      <MockWalletPill animateTo={230} pendingAnimateTo={500} />
      <MockHero subtitle="Lane-side, 40 seconds" />
    </div>
  )
}

/* ════════════════════════ SCENE: EARNINGS ════════════════════════ */

// EarningsCard with auto-cycling switcher. Today → Week → Month every 1.8s,
// numbers transition between values.
function SceneEarnings() {
  const periods: Array<{
    key: "Today" | "Week" | "Month"
    estimates: number
    earned: number
    paid: number
    pending: number
    pendingCount: number
  }> = [
    { key: "Today", estimates: 3, earned: 15, paid: 1, pending: 50, pendingCount: 1 },
    { key: "Week", estimates: 11, earned: 55, paid: 3, pending: 150, pendingCount: 3 },
    { key: "Month", estimates: 18, earned: 90, paid: 6, pending: 500, pendingCount: 10 },
  ]
  const [idx, setIdx] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % periods.length), 1800)
    return () => clearInterval(t)
  }, [periods.length])
  const p = periods[idx]

  return (
    <div className="space-y-3">
      <MockTopStrip showStreak={false} compact />
      <MockWalletPill animateTo={230} pendingAnimateTo={500} compact />
      <div className="relative rounded-2xl border border-zinc-200/70 bg-white shadow-sm overflow-hidden">
        <div className="p-3.5">
          {/* Switcher */}
          <div className="flex items-center gap-1 mb-3 p-1 rounded-lg bg-zinc-100 w-fit">
            {periods.map((per, i) => (
              <span
                key={per.key}
                className={cx(
                  "px-2.5 h-6 rounded-md text-[10px] font-bold uppercase tracking-wide transition-all duration-200 grid place-items-center",
                  i === idx
                    ? "bg-white text-zinc-900 shadow-sm"
                    : "text-zinc-500",
                )}
              >
                {per.key}
              </span>
            ))}
          </div>
          {/* Two columns */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-1">
                Estimates
              </p>
              <AnimatedNumber
                value={p.estimates}
                className="block text-[28px] font-black leading-none tracking-tight tabular-nums text-zinc-900"
              />
              <p className="text-[10px] text-zinc-500 mt-1 leading-tight">
                <span className="font-semibold text-emerald-700">{p.paid} paid</span>{" "}
                by body shop
              </p>
            </div>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-emerald-700 mb-1">
                Earned
              </p>
              <span className="block text-[28px] font-black leading-none tracking-tight tabular-nums bg-gradient-to-br from-emerald-600 to-emerald-800 bg-clip-text text-transparent">
                <span aria-hidden>$</span>
                <AnimatedNumber value={p.earned} className="inline" />
              </span>
              <p className="text-[10px] text-zinc-500 mt-1 leading-tight">
                Settled in your wallet
              </p>
            </div>
          </div>
          {/* Pending pill */}
          <div className="mt-2.5 inline-flex items-center gap-1.5 h-6 pl-1.5 pr-2 rounded-full border border-amber-200 bg-amber-50 text-amber-900 text-[10.5px] font-semibold">
            <span className="size-1.5 rounded-full bg-amber-500" aria-hidden />
            <span className="tabular-nums font-bold">
              $<AnimatedNumber value={p.pending} className="inline" />
            </span>
            <span className="opacity-80">pending on {p.pendingCount} open</span>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ════════════════════════ SCENE: ESTIMATE MORPH ════════════════════════ */

// A single estimate row that morphs Pending → Paid every ~2.5s. When it
// flips Paid, a +$50 chip floats up and the wallet pill above ticks up.
function SceneEstimateMorph({
  perEstimate,
  perPaid,
}: {
  perEstimate: number
  perPaid: number
}) {
  const [paid, setPaid] = useState(false)
  const [walletValue, setWalletValue] = useState(230)

  useEffect(() => {
    const t = setInterval(() => {
      setPaid((wasPaid) => {
        const next = !wasPaid
        if (next) setWalletValue((v) => v + perPaid)
        else setWalletValue(230)
        return next
      })
    }, 2600)
    return () => clearInterval(t)
  }, [perPaid])

  return (
    <div className="space-y-3">
      <MockTopStrip showStreak={false} compact />
      <MockWalletPill animateTo={walletValue} pendingAnimateTo={500} compact />

      <div className="relative rounded-2xl border border-zinc-200/70 bg-white shadow-sm overflow-hidden">
        <div className="px-4 pt-3 pb-2 flex items-center justify-between">
          <p className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-zinc-400">
            Your recent estimates
          </p>
        </div>
        <div className="px-2 pb-2 space-y-1">
          {/* The animated row */}
          <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-zinc-50/40">
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-semibold text-zinc-900 truncate">
                2024 Buick Enclave
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <motion.span
                  layout
                  className={cx(
                    "inline-flex items-center px-1.5 h-4 rounded text-[9px] font-bold uppercase tracking-wider border transition-colors duration-500",
                    paid
                      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                      : "bg-amber-50 text-amber-800 border-amber-200",
                  )}
                  transition={{ duration: 0.4 }}
                >
                  {paid ? "Paid" : "Pending"}
                </motion.span>
                <span className="text-[10px] text-zinc-400">#420</span>
              </div>
            </div>
            <div className="text-right shrink-0 relative">
              <p className="text-[12px] font-bold tabular-nums text-emerald-700">
                +${perEstimate}
              </p>
              <AnimatePresence mode="wait">
                {paid ? (
                  <motion.p
                    key="paid"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.3 }}
                    className="text-[9.5px] text-emerald-700 tabular-nums"
                  >
                    +${perPaid} paid
                  </motion.p>
                ) : (
                  <motion.p
                    key="pending"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.3 }}
                    className="text-[9.5px] text-amber-700 tabular-nums"
                  >
                    +${perPaid} pending
                  </motion.p>
                )}
              </AnimatePresence>
              {/* Floating +$50 when it flips paid */}
              <AnimatePresence>
                {paid && (
                  <motion.span
                    key="float"
                    initial={{ opacity: 0, y: 0 }}
                    animate={{ opacity: [0, 1, 1, 0], y: -60 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    className="absolute right-0 -top-1 text-[12px] font-black text-emerald-700 tabular-nums pointer-events-none"
                  >
                    +${perPaid}
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
            <ChevronRight className="size-3.5 text-zinc-300 shrink-0" aria-hidden />
          </div>
          {/* Two static rows for visual grounding */}
          <DimRow label="2023 Honda Civic" status="Paid" />
          <DimRow label="2022 Chevrolet Malibu" status="Pending" />
        </div>
      </div>
    </div>
  )
}

function DimRow({ label, status }: { label: string; status: "Paid" | "Pending" }) {
  const isPaid = status === "Paid"
  return (
    <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl opacity-50">
      <div className="min-w-0 flex-1">
        <p className="text-[11.5px] font-semibold text-zinc-700 truncate">{label}</p>
        <span
          className={cx(
            "inline-flex items-center px-1.5 h-4 mt-0.5 rounded text-[9px] font-bold uppercase tracking-wider border",
            isPaid
              ? "bg-emerald-50 text-emerald-800 border-emerald-200"
              : "bg-amber-50 text-amber-800 border-amber-200",
          )}
        >
          {status}
        </span>
      </div>
      <p className="text-[11.5px] font-bold tabular-nums text-emerald-700">+$5</p>
    </div>
  )
}

/* ════════════════════════ SCENE: CONTESTS ════════════════════════ */

// The Competitions card collapsed, then unfurling. Every module the
// manager enables is auto-active, so rows carry a Live chip — there is no
// Join button to demo. Continuous loop so the user sees the unfurl
// regardless of when they land on this slide.
function SceneContests({ count }: { count: number }) {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const first = setTimeout(() => setOpen(true), 700)
    const loop = setInterval(() => {
      setOpen(false)
      setTimeout(() => setOpen(true), 700)
    }, 5000)
    return () => {
      clearTimeout(first)
      clearInterval(loop)
    }
  }, [])

  const sampleRows: { icon: React.ComponentType<{ className?: string }>; tone: "amber" | "sky" | "indigo" | "emerald"; title: string; hint: string; prize: number }[] = [
    { icon: Trophy01, tone: "amber", title: "Daily scan champion", hint: "Most valid scans today.", prize: 25 },
    { icon: CalendarCheck01, tone: "indigo", title: "Streak bonus", hint: "5 workdays in a row.", prize: 50 },
    { icon: Target04, tone: "emerald", title: "Team goal bonus", hint: "Everyone wins together.", prize: 50 },
  ].slice(0, Math.max(2, Math.min(3, count)))

  return (
    <div className="space-y-3">
      <MockTopStrip showStreak={false} compact />
      <MockWalletPill animateTo={230} pendingAnimateTo={500} compact />
      <div className="relative">
        <motion.button
          type="button"
          layout
          className={cx(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl border shadow-sm text-left transition-all duration-200",
            open
              ? "border-zinc-300 bg-white rounded-b-none border-b-0"
              : "border-amber-200/40 bg-gradient-to-br from-amber-50/30 to-white",
          )}
        >
          <div className="size-8 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center shadow-md shadow-amber-500/30 shrink-0">
            <Trophy01 className="size-3.5 text-white" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[12px] font-semibold text-zinc-900 leading-tight">
              Competitions
            </p>
            <p className="text-[10px] text-zinc-500 leading-tight">
              {count} running automatically at your dealership.
            </p>
          </div>
          <motion.div
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.3 }}
            className="text-zinc-400 shrink-0"
          >
            <ChevronRight className="size-4 rotate-90" aria-hidden />
          </motion.div>
        </motion.button>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              style={{ overflow: "hidden" }}
              className="rounded-b-2xl border border-zinc-300 border-t-0 bg-white"
            >
              <div className="p-1.5">
                {sampleRows.map((r) => {
                  const Icon = r.icon
                  const tileCls =
                    r.tone === "amber"
                      ? "from-amber-400 to-amber-600 shadow-amber-500/40"
                      : r.tone === "sky"
                        ? "from-sky-400 to-sky-600 shadow-sky-500/40"
                        : r.tone === "indigo"
                          ? "from-indigo-400 to-indigo-600 shadow-indigo-500/40"
                          : "from-emerald-400 to-emerald-600 shadow-emerald-500/40"
                  return (
                    <div
                      key={r.title}
                      className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg"
                    >
                      <div
                        className={cx(
                          "size-8 rounded-lg grid place-items-center shrink-0 shadow-md bg-gradient-to-br",
                          tileCls,
                        )}
                      >
                        <Icon className="size-3.5 text-white" aria-hidden />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[12px] font-semibold text-zinc-900 leading-tight">
                          {r.title}
                        </p>
                        <p className="text-[10px] text-zinc-500 leading-tight truncate">
                          {r.hint}
                        </p>
                      </div>
                      <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded-md border border-amber-200/70 bg-amber-50 text-amber-900 text-[10px] font-bold shrink-0">
                        ${r.prize}
                      </span>
                      <span className="inline-flex items-center gap-1 h-6 px-2 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-800 text-[10px] font-bold uppercase tracking-wide shrink-0">
                        <span className="size-1.5 rounded-full bg-emerald-500" aria-hidden />
                        Live
                      </span>
                    </div>
                  )
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

/* ════════════════════════ SCENE: HERO ════════════════════════ */

// The Start-Estimate hero glowing. Mirrors the real dashboard's hero
// closely so the advisor recognizes the button when they land.
function SceneHero({
  perEstimate,
  perPaid,
}: {
  perEstimate: number
  perPaid: number
}) {
  const subtitle =
    perEstimate > 0 && perPaid > 0
      ? `+$${perEstimate} on save · +$${perPaid} when paid`
      : perEstimate > 0
        ? `+$${perEstimate} on save`
        : "40 seconds, lane-side"

  return (
    <div className="space-y-3">
      <MockTopStrip showStreak={false} compact />
      <MockWalletPill animateTo={230} pendingAnimateTo={500} compact />

      {/* The hero — emerald gradient, shimmer, pulsing */}
      <motion.div
        animate={{ scale: [1, 1.015, 1] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        className="relative"
      >
        <div
          aria-hidden
          className="absolute -inset-1 rounded-[24px] bg-gradient-to-br from-emerald-300/40 via-emerald-200/20 to-transparent blur-xl"
        />
        <div className="relative block overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-800 shadow-xl shadow-emerald-700/30">
          <div
            aria-hidden
            className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_55%)]"
          />
          <motion.span
            aria-hidden
            initial={{ x: "-100%" }}
            animate={{ x: "200%" }}
            transition={{ duration: 2.4, ease: "easeInOut", repeat: Infinity, repeatDelay: 1.5 }}
            className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/15 to-transparent skew-x-12 pointer-events-none"
          />
          <div className="relative flex items-center gap-3 p-4">
            <div className="size-11 rounded-xl bg-white/15 backdrop-blur grid place-items-center shrink-0">
              <ZapFast className="size-5 text-white" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-100/80">
                Action
              </p>
              <p className="text-[16px] font-black text-white leading-tight tracking-tight">
                Start an estimate
              </p>
              <p className="text-[11px] text-emerald-50/90 mt-0.5 leading-tight">
                {subtitle}
              </p>
            </div>
            <ArrowRight className="size-4 text-white/80 shrink-0" aria-hidden />
          </div>
        </div>
      </motion.div>
    </div>
  )
}

/* ════════════════════════ MOCK PIECES ════════════════════════ */

// Faithful-but-condensed versions of the real dashboard atoms. They share
// the same visual language (rounded-2xl, emerald accents, gradient pills)
// so the advisor recognizes them on the actual dashboard.

function MockTopStrip({
  showStreak,
  compact = false,
}: {
  showStreak: boolean
  compact?: boolean
}) {
  return (
    <div className={cx("flex items-center justify-between gap-2", compact ? "mb-1" : "mb-1")}>
      <div className="min-w-0">
        <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-emerald-600">
          Wed, May 28
        </p>
        <p className="text-[15px] font-bold text-zinc-900 tracking-tight leading-tight mt-0.5">
          Mornin', John
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <div className="inline-flex items-center gap-1 h-6 px-1.5 rounded-full border border-emerald-200/70 bg-emerald-50/70 text-emerald-900 text-[10px] font-bold">
          <Coins03 className="size-3 opacity-80" aria-hidden />
          <span>$425</span>
        </div>
        {showStreak && (
          <div className="inline-flex items-center gap-1 h-6 px-1.5 rounded-full border border-amber-300/70 bg-amber-50/70 text-amber-900 text-[10px] font-bold">
            <span>11 days</span>
          </div>
        )}
      </div>
    </div>
  )
}

// The wallet glance pill — same shape as the real one, with the available
// counter ticking up from 0 to a target value on mount.
function MockWalletPill({
  animateTo,
  pendingAnimateTo,
  compact = false,
}: {
  animateTo: number
  pendingAnimateTo: number
  compact?: boolean
}) {
  void compact
  return (
    <div className="relative rounded-2xl border border-zinc-200/70 bg-white px-3 py-2.5 shadow-sm flex items-center gap-2.5">
      <div className="size-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center shadow-md shadow-emerald-600/30 shrink-0">
        <Wallet01 className="size-3.5 text-white" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-1.5">
          <span className="text-[15px] font-black text-zinc-900 leading-none tabular-nums">
            $<AnimatedNumber value={animateTo} className="inline" />
          </span>
          <span className="text-[10px] font-semibold text-zinc-500">available</span>
          <span className="text-[10px] text-zinc-400 tabular-nums ml-0.5">
            · $<AnimatedNumber value={pendingAnimateTo} className="inline" /> pending
          </span>
        </div>
        <p className="text-[9px] text-zinc-500 mt-0.5">Next payout in 4 days</p>
      </div>
      <ChevronRight className="size-3.5 text-zinc-400 shrink-0" aria-hidden />
    </div>
  )
}

// The Start-Estimate hero, condensed. Used inside scene-wallet so the
// first slide already shows the action surface (just not as the centerpiece).
function MockHero({ subtitle }: { subtitle: string }) {
  return (
    <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-800 shadow-lg shadow-emerald-700/25">
      <div className="relative flex items-center gap-3 p-3">
        <div className="size-9 rounded-lg bg-white/15 backdrop-blur grid place-items-center shrink-0">
          <ZapFast className="size-4 text-white" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-black text-white leading-tight tracking-tight">
            Start an estimate
          </p>
          <p className="text-[10.5px] text-emerald-50/90 leading-tight mt-0.5">
            {subtitle}
          </p>
        </div>
        <ArrowRight className="size-3.5 text-white/80 shrink-0" aria-hidden />
      </div>
    </div>
  )
}

/* ════════════════════════ ANIMATED NUMBER ════════════════════════ */

// Smoothly transitions from previous → next value using a spring. Used
// for the wallet counter, estimate counts, and earnings totals in scenes.
function AnimatedNumber({ value, className }: { value: number; className?: string }) {
  const mv = useMotionValue(0)
  const spring = useSpring(mv, { duration: 700, bounce: 0.05 })
  const display = useTransform(spring, (v) => Math.round(v).toLocaleString("en-US"))
  useEffect(() => {
    mv.set(value)
  }, [mv, value])
  return <motion.span className={className}>{display}</motion.span>
}

/* ════════════════════════ HELPERS ════════════════════════ */

function moneyOf(b: DealershipRewardBundle | undefined): number {
  if (!b?.monetary?.enabled) return 0
  return b.monetary.amount ?? 0
}

// Number of auto-active competition modules the dealership is running —
// drives the competitions slide's "N competitions" headline.
function countEnabledContests(g: DealershipGoalsRewards): number {
  return Object.values(g.competitions ?? {}).filter((c) => c?.enabled).length
}
